// ============================================================================
// MessagesPanel — the TRAINER side of the 1:1 thread, on the client-detail page
// Messages tab (Wave 5).
// ----------------------------------------------------------------------------
// Owns the TRAINER realm's data (its own React Query hooks + keys, scoped to one
// owned client by id) and the mark-read-on-open behaviour, then delegates the
// whole chat presentation to the shared <MessageThread> component (the same
// composer + bubble list the client portal uses), parameterized realm='trainer'
// so the trainer's own messages align right.
//   • body rendered as PLAIN TEXT, unicode emoji, image attach ≤2 MB, "Seen"
//     receipt — all in the shared component.
//   • marks the thread read on open (and when new client messages arrive), which
//     clears this client's roster + tab unread badge.
//   • live-syncs via the trainer WS (useLiveSync, mounted in the AppShell): a
//     message/read from the client updates the thread + badges with no reload.
// ============================================================================
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardHeader, MessageThread } from "../../components";
import type { MessageThreadHandlers } from "../../components";
import { messagesApi } from "../../api";
import type { Message } from "../../api";
import {
  useClientThread,
  useMarkClientThreadRead,
  useSendClientThreadAttachment,
  useSendClientThreadMessage,
} from "../../hooks/messages";

export function MessagesPanel({ clientId }: { clientId: number }) {
  const { t } = useTranslation("messages");

  const thread = useClientThread(clientId);
  const sendMsg = useSendClientThreadMessage(clientId);
  const sendAttachment = useSendClientThreadAttachment(clientId);
  const markRead = useMarkClientThreadRead(clientId);

  const messages = useMemo<Message[]>(
    () => thread.data?.messages ?? [],
    [thread.data],
  );
  const unread = thread.data?.unread ?? 0;

  // Mark the thread read on open and whenever new CLIENT messages arrive (the
  // server only stamps the counterpart's unread, never the trainer's own).
  useEffect(() => {
    if (unread > 0 && !markRead.isPending) {
      markRead.mutate();
    }
    // markRead is stable enough; intentionally key on the unread count only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread]);

  const handlers: MessageThreadHandlers = {
    send: (body) => sendMsg.mutateAsync({ body }),
    sending: sendMsg.isPending,
    sendAttachment: (file, body) => sendAttachment.mutateAsync({ file, body }),
    attaching: sendAttachment.isPending,
    attachmentUrl: (photoId) => messagesApi.attachmentUrl(clientId, photoId),
  };

  return (
    <Card>
      <CardHeader title={t("title")} subtitle={t("trainer.subtitle")} />
      <MessageThread
        realm="trainer"
        messages={messages}
        loading={thread.isLoading}
        errored={thread.isError}
        handlers={handlers}
        emptyLabel={t("emptyTrainer")}
        className="h-[64vh]"
      />
    </Card>
  );
}
